import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import { Navigate } from 'react-router-dom';
import {
  searchUsers,
  getUserModeration,
  adminModerationAction,
  getModerationLogs
} from '../../api/adminModeration';

export default function AdminModerationConsole() {
  const { user } = useContext(AuthContext);
  
  // Access control
  if (!user || !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModData, setUserModData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsFilter, setLogsFilter] = useState({ type: '', target_id: '' });
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Action state
  const [actionInProgress, setActionInProgress] = useState(false);
  
  // Search users
  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const data = await searchUsers(searchQuery);
      setSearchResults(data.items || []);
    } catch (err) {
      console.error('Search failed:', err);
      alert('Search failed');
    } finally {
      setSearching(false);
    }
  };
  
  // Select user
  const handleSelectUser = async (userId) => {
    setLoadingUser(true);
    try {
      const data = await getUserModeration(userId);
      setSelectedUser(data.user);
      setUserModData(data.moderation);
      setLogsFilter({ ...logsFilter, target_id: userId });
    } catch (err) {
      console.error('Failed to load user:', err);
      alert('Failed to load user data');
    } finally {
      setLoadingUser(false);
    }
  };
  
  // Load logs
  useEffect(() => {
    const loadLogs = async () => {
      setLoadingLogs(true);
      try {
        const params = {
          page: logsPage,
          page_size: 25,
          ...logsFilter
        };
        const data = await getModerationLogs(params);
        setLogs(data.items || []);
        setLogsTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to load logs:', err);
      } finally {
        setLoadingLogs(false);
      }
    };
    
    loadLogs();
  }, [logsPage, logsFilter]);
  
  // Perform action
  const performAction = async (action, confirmMessage) => {
    if (!selectedUser) return;
    
    if (!window.confirm(confirmMessage)) return;
    
    const reason = prompt('Reason (optional):');
    
    setActionInProgress(true);
    try {
      await adminModerationAction(selectedUser.id, action, reason);
      alert('Action completed successfully');
      // Reload user data
      await handleSelectUser(selectedUser.id);
    } catch (err) {
      console.error('Action failed:', err);
      alert('Action failed: ' + err.message);
    } finally {
      setActionInProgress(false);
    }
  };
  
  return (
    <div className="min-h-screen surface-page">
      {/* Top bar */}
      <div className="surface-card border-b border-gray-200 dark:border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">🛡️ Moderation Console</h1>
          
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email..."
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
            <button
              type="submit"
              disabled={searching || searchQuery.length < 2}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </form>
          
          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="mt-2 surface-card border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((u) => (
                <button
                  key={u.id}
                  onClick={() => {
                    handleSelectUser(u.id);
                    setSearchResults([]);
                    setSearchQuery('');
                  }}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">{u.email}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{u.user_type} {u.is_admin && '• ADMIN'}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Main content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left pane: Selected user */}
          <div className="lg:col-span-1">
            <div className="surface-card rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Selected User</h2>
              
              {!selectedUser && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Search and select a user to view details</p>
              )}
              
              {loadingUser && (
                <p className="text-sm text-gray-500">Loading...</p>
              )}
              
              {selectedUser && userModData && (
                <div className="space-y-4">
                  {/* User info */}
                  <div>
                    <div className="text-sm font-medium text-gray-700">Email</div>
                    <div className="text-sm text-gray-900">{selectedUser.email}</div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-gray-700">User ID</div>
                    <div className="text-xs text-gray-500 font-mono">{selectedUser.id}</div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-gray-700">Type</div>
                    <div className="text-sm text-gray-900">{selectedUser.user_type}</div>
                  </div>
                  
                  {/* Moderation data */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-700">Strikes:</span>
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold rounded">
                        {userModData.moderation_strikes}
                      </span>
                    </div>
                    
                    {userModData.shadowbanned && (
                      <div className="mb-2">
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                          SHADOWBANNED
                        </span>
                      </div>
                    )}
                    
                    {userModData.lounge_blocked_until && (
                      <div className="text-sm text-gray-700 mb-2">
                        <span className="font-medium">Lounge banned until:</span>
                        <div className="text-xs text-red-600">
                          {new Date(userModData.lounge_blocked_until).toLocaleString()}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Blocked users:</span> {userModData.blocked_users.length}
                    </div>
                    
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Blocked by:</span> {userModData.blocked_by_count} users
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    <button
                      onClick={() => performAction('kick_72h', 'Kick this user from lounge for 72 hours?')}
                      disabled={actionInProgress}
                      className="w-full px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50"
                    >
                      Kick 72h from Lounge
                    </button>
                    
                    {!userModData.shadowbanned ? (
                      <button
                        onClick={() => performAction('shadowban', 'Shadowban this user?')}
                        disabled={actionInProgress}
                        className="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Shadowban
                      </button>
                    ) : (
                      <button
                        onClick={() => performAction('clear_shadowban', 'Remove shadowban from this user?')}
                        disabled={actionInProgress}
                        className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Remove Shadowban
                      </button>
                    )}
                    
                    <button
                      onClick={() => performAction('reset_lounge_ban', 'Reset lounge ban for this user?')}
                      disabled={actionInProgress}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Reset Lounge Ban
                    </button>
                    
                    <button
                      onClick={() => performAction('reset_strikes', 'Reset moderation strikes for this user?')}
                      disabled={actionInProgress}
                      className="w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50"
                    >
                      Reset Strikes
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Right pane: Logs */}
          <div className="lg:col-span-2">
            <div className="surface-card rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Moderation Logs</h2>
                
                {/* Filters */}
                <div className="flex gap-2">
                  <select
                    value={logsFilter.type}
                    onChange={(e) => setLogsFilter({ ...logsFilter, type: e.target.value })}
                    className="px-3 py-1 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="report">Report</option>
                    <option value="block">Block</option>
                    <option value="kick">Kick</option>
                    <option value="shadowban">Shadowban</option>
                  </select>
                </div>
              </div>
              
              {loadingLogs ? (
                <p className="text-sm text-gray-500">Loading logs...</p>
              ) : logs.length === 0 ? (
                <p className="text-sm text-gray-500">No logs found</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Date</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Reporter</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Target</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr key={log.id} className="border-b border-gray-100">
                            <td className="px-4 py-2 text-gray-600">
                              {new Date(log.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                log.type === 'report' ? 'bg-blue-100 text-blue-800' :
                                log.type === 'kick' ? 'bg-orange-100 text-orange-800' :
                                log.type === 'shadowban' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {log.type}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-600 text-xs font-mono">
                              {log.reporter_id ? log.reporter_id.substring(0, 8) : 'system'}
                            </td>
                            <td className="px-4 py-2 text-gray-600 text-xs font-mono">
                              {log.target_id.substring(0, 8)}
                            </td>
                            <td className="px-4 py-2 text-gray-600 max-w-xs truncate">
                              {log.reason}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {log.action_taken}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Page {logsPage} • Total: {logsTotal} logs
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLogsPage(Math.max(1, logsPage - 1))}
                        disabled={logsPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setLogsPage(logsPage + 1)}
                        disabled={logs.length < 25}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
